import {
  Divider,
  FormControl,
  FormControlProps,
  FormHelperText,
  TextField,
  TextFieldProps,
  useThemeProps,
} from '@mui/material';
import { FORM_FIELD_THEME_NAME } from '@onevision-resources/design-theme';
import { ReactNode, useEffect, useId, useRef } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  type UseControllerProps,
  useFormContext,
  type ValidationRule,
} from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { useWatchConditions } from 'src/components/Forms/use-validations';
import { useDebounce } from 'src/utils/hooks/use-debounce';

export type FormTextFieldProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'name' | 'textFieldProps'
> & {
  label: string;
  required?: boolean | string;
  pattern?: ValidationRule<RegExp>;
  rules?: UseControllerProps['rules'];
  textFieldProps?: Partial<TextFieldProps>;
  value?: string;
  underlined?: boolean;
  disabled?: boolean;
  onChangeDebounceDelay?: number;
  showErrorText?: boolean;
  /**
   * Render a static label above the input (v2 redesign look) instead of MUI's
   * floating label; also defaults `variant` to `outlined`. A boolean rather
   * than a `labelPlacement` enum to avoid colliding with the MUI
   * `FormControlLabel` `labelPlacement` on sibling members of the `FormField`
   * union (which breaks the `getFormField` prop spread).
   */
  labelAbove?: boolean;
  /** Always-visible helper text under the input, unlike the error-only text. */
  description?: ReactNode;
} & CommonFormFieldProps<'textfield', string>;

// we need this function to be able to properly use FormTextField
// with external refs, it merges incoming ref with local one so
// we can support focusing input outside by using ref.current.focus() inside forms
const mergeRefs =
  (...refs: (React.Ref<HTMLInputElement> | undefined)[]) =>
  (node: HTMLInputElement | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(node);
      } else {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    });
  };

interface RenderTextFieldProps {
  field: ControllerRenderProps<FieldValues, string>;
  textFieldProps?: Partial<TextFieldProps>;
  label?: string;
  variant: TextFieldProps['variant'];
  size?: TextFieldProps['size'];
  error: boolean;
  value?: string;
  disabled?: boolean;
  onChangeDebounceDelay?: number;
}

const RenderTextField = ({
  field,
  textFieldProps,
  label,
  variant,
  size,
  error,
  value,
  onChangeDebounceDelay = 0,
  disabled = false,
}: RenderTextFieldProps) => {
  const localRef = useRef<HTMLInputElement | null>(null);
  const combinedRef = mergeRefs(textFieldProps?.inputRef, field.ref, localRef);

  useEffect(() => {
    let timer: string | number | NodeJS.Timeout | undefined;
    if (textFieldProps?.autoFocus) {
      timer = setTimeout(() => {
        localRef.current?.focus();
      }, 100);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [textFieldProps?.autoFocus]);

  const debouncedChange = useDebounce((event: React.ChangeEvent<HTMLInputElement>) => {
    textFieldProps?.onChange?.(event);
  }, onChangeDebounceDelay);

  return (
    <TextField
      error={error}
      variant={variant}
      size={size}
      label={label}
      disabled={disabled}
      {...field}
      {...textFieldProps}
      onChange={(event) => {
        field.onChange(event);
        if (onChangeDebounceDelay) {
          debouncedChange(event);
        } else {
          textFieldProps?.onChange?.(event);
        }
      }}
      onBlur={(event) => {
        field.onBlur();
        textFieldProps?.onBlur?.(event);
      }}
      value={(field.value as string) ?? value}
      inputRef={combinedRef}
      InputProps={{
        // MUI doesn't support `disableUnderline` for `outlined` input variant ¯\_(ツ)_/¯
        ...((textFieldProps?.variant ?? variant) !== 'outlined'
          ? { disableUnderline: true }
          : {}),
        ...textFieldProps?.InputProps,
      }}
    />
  );
};

export const FormTextField = (inProps: FormTextFieldProps) => {
  const props = useThemeProps({ props: inProps, name: FORM_FIELD_THEME_NAME });
  const {
    name,
    label,
    value,
    required,
    pattern,
    rules,
    textFieldProps = {},
    variant: variantProp,
    size,
    showsIf = [],
    underlined = false,
    disabled = false,
    onChangeDebounceDelay = 0,
    showErrorText = false,
    labelAbove = false,
    description,
    ...rest
  } = props;
  const variant = variantProp ?? (labelAbove ? 'outlined' : 'standard');
  const generatedInputId = useId();
  const inputId = textFieldProps.id ?? generatedInputId;
  const { control } = useFormContext();

  const shouldShow = useWatchConditions({
    control,
    showsIf,
  });

  if (!shouldShow) {
    return null;
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required, pattern, ...rules }}
      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;
        const hasStringMsg = typeof errorMsg === 'string';
        let helperText: string | null = null;
        if (errorMsg) {
          helperText = hasStringMsg ? errorMsg : String(errorMsg);
        }

        return (
          <FormControl variant={variant} size={size} {...rest} error={!!fieldState.error}>
            {labelAbove && !!label ? (
              <FormFieldLabel htmlFor={inputId} disabled={disabled}>
                {label}
              </FormFieldLabel>
            ) : null}
            <RenderTextField
              field={field}
              textFieldProps={labelAbove ? { id: inputId, ...textFieldProps } : textFieldProps}
              label={labelAbove ? undefined : label}
              variant={variant}
              size={size}
              error={!!fieldState.error}
              value={value}
              disabled={disabled}
              onChangeDebounceDelay={onChangeDebounceDelay}
            />
            {!!underlined && !rest.hidden && (
              <Divider sx={{ alignSelf: 'stretch', marginTop: '0.3rem' }} />
            )}
            {description ? <FormHelperText error={false}>{description}</FormHelperText> : null}
            {!!showErrorText && !!helperText ? (
              <FormHelperText error={!!fieldState.error}>{helperText}</FormHelperText>
            ) : null}
          </FormControl>
        );
      }}
    />
  );
};
